"use client";

/**
 * Core auth hook — internal implementation behind `AuthProvider`.
 *
 * NOT exported from the auth feature barrel. Every consumer outside
 * the auth feature uses `useAuthContext()` (which reads the single
 * `useAuth()` state via React Context). This keeps the auth state a
 * singleton with one writer — no `globalAuthListeners` dance needed.
 *
 * Access token lives in memory (see `lib/session.ts`); refresh token
 * lives in the `hh_refresh` HttpOnly cookie owned by the backend.
 */

import { useCallback, useEffect, useState } from "react";

import * as authApi from "../api";
import {
  clearAccessToken,
  forceSignOut,
  setAccessToken,
} from "../lib/session";
import { tryRefresh } from "@/shared/lib/api/client";
import type {
  AuthStatus,
  LoginCredentials,
  RegisterCredentials,
  User,
} from "../types";

interface AuthState {
  user: User | null;
  status: AuthStatus;
}

/**
 * One backend round-trip on cold page load:
 *   POST /auth/refresh — sends hh_refresh cookie, gets { access, user }
 *
 * The /auth/me call we used to make right after is GONE — the refresh
 * response already includes the user (see TokenResponse in
 * backend/src/auth/schemas.py). Cuts cold auth from 2 round-trips to 1.
 *
 * Resolves to the state the answer implies; the hook applies it.
 */
async function resolveSession(): Promise<AuthState> {
  try {
    const { ok, user } = await tryRefresh();
    return ok && user
      ? { user, status: "authenticated" }
      : { user: null, status: "unauthenticated" };
  } catch (err) {
    if (err instanceof authApi.NetworkError) {
      return { user: null, status: "server-unreachable" };
    }
    clearAccessToken();
    return { user: null, status: "unauthenticated" };
  }
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    status: "loading",
  });

  useEffect(() => {
    void resolveSession().then(setState);
  }, []);

  const login = useCallback(async (creds: LoginCredentials) => {
    const result = await authApi.login(creds);
    setAccessToken(result.access_token, result.expires_in, result.user.id);
    setState({ user: result.user, status: "authenticated" });
    return result.user;
  }, []);

  const register = useCallback(async (creds: RegisterCredentials) => {
    const result = await authApi.register(creds);
    setAccessToken(result.access_token, result.expires_in, result.user.id);
    setState({ user: result.user, status: "authenticated" });
    return result.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // best-effort server-side logout — proceed regardless
    }
    setState({ user: null, status: "unauthenticated" });
    forceSignOut("user-logout");
  }, []);

  const retry = useCallback(async () => {
    setState({ user: null, status: "loading" });
    setState(await resolveSession());
  }, []);

  return {
    user: state.user,
    status: state.status,
    isLoading: state.status === "loading",
    isAuthenticated: state.status === "authenticated",
    login,
    register,
    logout,
    retry,
  };
}
